import { useContext } from "react";
import { formatToShortDate, formatUniversalNumber } from "../../../lib/utils";
import { ListingData } from "../types";
import { GeneralLedgerContext } from "../../../providers/GeneralLedgerProvider";
import { TableSkeleton } from "../../../components/Skeleton/TableSkeleton";
import { DataTable } from "../../../components/ui/data-table";

interface ListingTableRowProps {
  listingData: ListingData[];
}

const ListingTableRow = ({ listingData }: ListingTableRowProps) => {
  const {
    isListingDateLoading,
    listingPagination,
    setListingPagination,
    chartofaccountsData,
    chartListing,
    getListingData,
  } = useContext(GeneralLedgerContext);

  // Column definitions for the DataTable
  const columns = [
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }: any) => {
        return <span>{formatToShortDate(row.original.date)}</span>;
      },
    },
    {
      accessorKey: "journalId",
      header: "Voucher ID #",
      size: 150,
      cell: ({ row }: any) => {
        return <span className="text-center">{row.original.journalId}</span>;
      },
    },
    {
      accessorKey: "source",
      header: "Source",
      cell: ({ row }: any) => {
        const { source, externalId } = row.original;
        // Show "source - externalId" only if externalId is not null/undefined/empty
        return (
          <span>
            {source}
            {externalId != null && externalId !== "" ? ` - ${externalId}` : ""}
          </span>
        );
      },
    },
    {
      accessorKey: "debit",
      header: () => (
        <div className="text-right" style={{ width: "80px" }}>
          Debit
        </div>
      ),
      size: 80,
      minSize: 80,
      maxSize: 80,
      cell: ({ row }: any) => {
        return (
          <span style={{ width: "80px" }} className="text-right block">
            {formatUniversalNumber(row.original.debit)}
          </span>
        );
      },
    },
    {
      accessorKey: "credit",
      header: () => (
        <div className="text-right" style={{ width: "80px" }}>
          Credit
        </div>
      ),
      size: 80,
      minSize: 80,
      maxSize: 80,
      enableResizing: false,
      cell: ({ row }: any) => {
        return (
          <span style={{ width: "80px" }} className="text-right block">
            {formatUniversalNumber(row.original.credit)}
          </span>
        );
      },
    },
    {
      accessorKey: "runningBalance",
      header: () => (
        <div className="text-right" style={{ width: "130px" }}>
          Running Balance
        </div>
      ),
      size: 130,
      minSize: 130,
      maxSize: 130,
      enableResizing: false,
      cell: ({ row }: any) => {
        return (
          <span style={{ width: "130px" }} className="text-right block">
            {formatUniversalNumber(row.original.runningBalance)}
          </span>
        );
      },
    },
  ];

  // Helper function to find an item in the chart data
  const findItem = (items: any[]): any => {
    const findInLevel = (items: any[], currentLevel: string): any => {
      for (const item of items) {
        // For parent level items (uses categoryId)
        if (
          currentLevel === "parent" &&
          item.name === chartListing.listingId &&
          item.type === item.name &&
          item.sub_type === item.name
        ) {
          return item;
        }

        // For child level items (uses accountSubTypeId)
        if (
          currentLevel === "child" &&
          item.children?.length > 0 &&
          (item.name === chartListing.listingId ||
            item.sub_type === chartListing.listingId)
        ) {
          return item;
        }

        // For grandchild level items (uses accountId)
        if (
          currentLevel === "grandchild" &&
          !item.children?.length &&
          (item.name === chartListing.listingId ||
            item.sub_type === chartListing.listingId)
        ) {
          return item;
        }

        // Recursively search in children
        if (item.children?.length > 0) {
          const found = findInLevel(item.children, currentLevel);
          if (found) return found;
        }
      }
      return null;
    };

    // Try finding the item at each level in order
    let found =
      findInLevel(items, "parent") || // First try parent level
      findInLevel(items, "child") || // Then child level
      findInLevel(items, "grandchild"); // Finally grandchild level

    return found || undefined;
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    // Find the current item to preserve its level and ID type
    const item = findItem(chartofaccountsData);

    if (item) {
      // Update pagination state
      setListingPagination({
        ...listingPagination,
        page,
      });

      // Call getListingData with the found item to preserve ID type
      getListingData(
        chartListing.listingId,
        item,
        page,
        listingPagination.pageSize || 10
      );
    }
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    // Update pagination with new page size and reset to page 1
    setListingPagination({
      ...listingPagination,
      page: 1,
      pageSize: size,
    });

    // For page size changes, we need to explicitly call getListingData
    // since the useEffect only handles page number changes beyond page 1
    const item = findItem(chartofaccountsData);
    if (item) {
      getListingData(item.name || "", item, 1, size);
    }
  };

  // Get the current page size from the pagination state or default to 10
  const currentPageSize = listingPagination.pageSize || 10;

  return (
    <>
      {isListingDateLoading ? (
        <TableSkeleton tableTheme="gray" />
      ) : (
        <DataTable
          columns={columns}
          data={listingData}
          showPagination={true}
          pageSize={currentPageSize}
          currentPage={listingPagination.page}
          totalPages={listingPagination.totalPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRecords={listingPagination.totalRecords || 0}
          variant="transaction"
        />
      )}
    </>
  );
};

export default ListingTableRow;
